import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useSession } from '@/contexts/LitSessionProvider';
import { useMixpanel } from '@/lib/misc/mixpanel';
import usePaymentProvider from '@/hooks/deposit/usePaymentProvider';
import useGetLocation from '@/hooks/utilities/useGetLocation';
import useAppSettings, { AppConfigKeys, CoinbaseMappings } from '@/hooks/admin/useAppSettings';
type CoinbaseButtonProps = {
  presetFiatAmount: string;
};
export default function useCoinbaseButton({
  presetFiatAmount
}: CoinbaseButtonProps) {
  const {
    PKP
  } = useSession();
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const {
    selectedPaymentMethod
  } = usePaymentProvider();
  const {
    appConfig
  } = useAppSettings();
  const purchaseCurrency = CoinbaseMappings[appConfig?.[AppConfigKeys.onboardingCurrency]]?.name || CoinbaseMappings['SOL']?.name;
  const network = CoinbaseMappings[appConfig?.[AppConfigKeys.onboardingCurrency]]?.chain || CoinbaseMappings['SOL']?.chain;
  const coinbasePaymentMethod = () => {
    if (selectedPaymentMethod == 'card') return 'CARD';
    if (selectedPaymentMethod == 'bank') return 'ACH_BANK_ACCOUNT';
    if (selectedPaymentMethod == 'apple') return 'APPLE_PAY';
    if (selectedPaymentMethod == 'coinbase_account') return 'CRYPTO_ACCOUNT';
  };
  const {
    geoCountry: country,
    geoState: state
  } = useGetLocation();
  const [quoteLoading, setQuoteLoading] = useState(false);
  async function generateBuyQuote() {
    setQuoteLoading(true);
    try {
      const response = await fetch('/api/wrapper?target=https://api.developer.coinbase.com/onramp/v1/buy/quote', {
        method: 'POST',
        body: JSON.stringify({
          purchase_currency: purchaseCurrency,
          payment_amount: presetFiatAmount,
          payment_currency: 'USD',
          payment_method: coinbasePaymentMethod() == 'CRYPTO_ACCOUNT' ? 'CARD' : coinbasePaymentMethod(),
          country: country || 'US',
          subdivision: state || 'NY'
        })
      });
      const data = await response.json();
      console.log('coinbase quote', data);
      const quoteId = data?.quote_id;
      if (!quoteId) {
        throw new Error('Invalid quote id');
      }
      return data;
    } catch (e) {
      console.log('coinbase error', e);
    } finally {
      setQuoteLoading(false);
    }
  }
  useEffect(() => {
    if (presetFiatAmount) {
      generateBuyQuote();
    }
  }, [presetFiatAmount]);
  const handleOnPress = async () => {
    setQuoteLoading(true);
    LogToMixpanel({
      pkp: PKP,
      eventName: EVENTS.deposit.startDeposit,
      category: CATEGORIES.deposit,
      metaData: {
        type: 'coinbase'
      }
    });
    const quote = await generateBuyQuote();
    setQuoteLoading(false);
    if (!quote?.quote_id) {
      toast.info('We had an internal error, please refresh your screen and try again');
      return;
    }
    const walletAddress = purchaseCurrency?.toLowerCase()?.includes('sol') ? PKP?.solanaAddress : PKP.additionalEthAddress;
    const buyUrl = `https://pay.coinbase.com/buy/select-asset?appId=d7310100-f104-4a43-a74f-c06e2d018264&destinationWallets=[{"address":"${walletAddress}","blockchains":["${network}"]}]&defaultAsset=${purchaseCurrency}&defaultPaymentMethod=${coinbasePaymentMethod()}&fiatCurrency=USD&presetFiatAmount=${presetFiatAmount}&quoteId=${quote?.quote_id}`;
    console.log('coinbasebuyurl', buyUrl);
    window.open(buyUrl, '_blank');
  };
  return {
    quoteLoading,
    handleOnPress
  };
}